import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Search, Filter, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { Order, PaginationMeta } from '../types'
import {
  formatCurrency,
  formatDate,
  getOrderStatusLabel,
  getOrderStatusColor,
} from '../utils/helpers'
import Pagination from '../components/Pagination'
import LinePreviewPopup from '../components/LinePreviewPopup'

export default function Orders() {
  const { t } = useTranslation()
  const [orders, setOrders] = useState<Order[]>([])
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortField, setSortField] = useState<string | null>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [error, setError] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadOrders = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getOrders({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setOrders(list)
      setFilteredOrders(list)
      setPagination(pag)
      setError(null)
    } catch (err: unknown) {
      console.error('Erreur chargement commandes:', err)
      setError(getApiErrorKey(err, 'orders.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, limit])

  const filterAndSortOrders = useCallback(() => {
    let filtered = orders

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer_ref?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      const statusNum = parseInt(statusFilter, 10)
      filtered = filtered.filter((order) => {
        const s =
          typeof order.status === 'string' ? parseInt(order.status, 10) : Number(order.status)
        return s === statusNum
      })
    }

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const va = (a as unknown as Record<string, unknown>)[sortField] ?? ''
        const vb = (b as unknown as Record<string, unknown>)[sortField] ?? ''
        if (typeof va === 'number' && typeof vb === 'number')
          return sortDir === 'asc' ? va - vb : vb - va
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va))
      })
    }

    setFilteredOrders(filtered)
  }, [orders, searchTerm, statusFilter, sortField, sortDir])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])
  useEffect(() => {
    filterAndSortOrders()
  }, [filterAndSortOrders])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    const target = e.currentTarget // capture before setTimeout (React nullifies currentTarget after handler)
    hoverTimer.current = setTimeout(() => {
      setHoveredId(id)
      setHoveredRect(target.getBoundingClientRect())
    }, 400)
  }

  const handleRowMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoveredId(null)
    setHoveredRect(null)
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300 inline ml-1" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-primary-600 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-primary-600 inline ml-1" />
    )
  }

  const totalAmount = filteredOrders.reduce((sum, order) => sum + order.total_incl_tax, 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium mb-2">{t(error)}</p>
          <button
            onClick={loadOrders}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <p className="text-sm font-medium text-gray-600">{t('orders.totalOrders')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {pagination ? pagination.total : filteredOrders.length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <p className="text-sm font-medium text-gray-600">{t('orders.totalAmount')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(totalAmount)}</p>
        </div>
      </div>

      {!pagination && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('orders.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none appearance-none bg-white"
              >
                <option value="all">{t('common.allStatuses')}</option>
                <option value="0">{t('orders.statusDraft')}</option>
                <option value="1">{t('orders.statusValidated')}</option>
                <option value="2">{t('orders.statusInProgress')}</option>
                <option value="3">{t('orders.statusShipped')}</option>
                <option value="-1">{t('orders.statusCancelled')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>{t('orders.notFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {filteredOrders.map((order) => (
                <li key={order.id} className="p-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {pagination && (
                        <p className="text-xs text-gray-500 truncate">
                          {order.customer_name ?? '—'}
                        </p>
                      )}
                      <Link
                        to={`/orders/${order.id}${order._erp_id ? `?erp_id=${order._erp_id}` : ''}`}
                        className="font-medium text-primary-600 hover:text-primary-700 text-sm"
                      >
                        {order.ref}
                      </Link>
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getOrderStatusColor(order.status)}`}
                    >
                      {getOrderStatusLabel(order.status, t)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{formatDate(order.date)}</span>
                    <span className="font-semibold text-sm text-gray-900">
                      {formatCurrency(order.total_incl_tax)}
                    </span>
                  </div>
                  {order.delivery_date && (
                    <p className="text-xs text-gray-400">
                      {t('orders.colDelivery')} : {formatDate(order.delivery_date)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {pagination && (
                      <th
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('customer_name')}
                      >
                        {t('common.client')} <SortIcon field="customer_name" />
                      </th>
                    )}
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('ref')}
                    >
                      {t('orders.colRef')} <SortIcon field="ref" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date')}
                    >
                      {t('orders.colDate')} <SortIcon field="date" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('delivery_date')}
                    >
                      {t('orders.colDelivery')} <SortIcon field="delivery_date" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('total_incl_tax')}
                    >
                      {t('orders.colAmount')} <SortIcon field="total_incl_tax" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      {t('orders.colStatus')} <SortIcon field="status" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors"
                      onMouseEnter={(e) => handleRowMouseEnter(e, order.id)}
                      onMouseLeave={handleRowMouseLeave}
                    >
                      {pagination && (
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium whitespace-nowrap">
                          {order.customer_name ?? '—'}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <Link
                          to={`/orders/${order.id}${order._erp_id ? `?erp_id=${order._erp_id}` : ''}`}
                          className="font-medium text-primary-600 hover:text-primary-700"
                        >
                          {order.ref}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDate(order.date)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {order.delivery_date ? formatDate(order.delivery_date) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatCurrency(order.total_incl_tax)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getOrderStatusColor(order.status)}`}
                        >
                          {getOrderStatusLabel(order.status, t)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {pagination && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      {/* Hover line preview */}
      {hoveredId && hoveredRect && (
        <LinePreviewPopup id={hoveredId} type="order" anchorRect={hoveredRect} />
      )}
    </div>
  )
}
